import React, { useState, useEffect } from 'react';
import { CheckCircle, Scale, Users, Award, Target, BookOpen, Briefcase } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const AboutPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    document.title = "About Us - IP Partners Inc";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brown-100 to-brown-50 pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-brown-900 mb-4">
            About IP Partners Inc
          </h1>
          <p className="text-xl text-brown-700 max-w-3xl mx-auto">
            Your trusted partner for comprehensive legal services across business, compliance, and intellectual property matters
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-brown-900 mb-6">Our Mission</h2>
              <p className="text-lg text-brown-700 leading-relaxed mb-6">
                At IP Partners Inc, we believe everyone deserves access to quality legal services. 
                Our mission is to connect individuals and businesses with experienced, licensed attorneys 
                who can provide expert guidance and representation.
              </p>
              <p className="text-lg text-brown-700 leading-relaxed mb-6">
                We strive to make legal services accessible, transparent, and affordable while maintaining 
                the highest standards of professionalism and client care.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-brown-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-brown-800 transition-colors duration-200"
              >
                Schedule Consultation
              </button>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/8112172/pexels-photo-8112172.jpeg"
                alt="Professional handshake"
                className="rounded-xl shadow-xl w-full h-[450px] object-cover ring-2 ring-brown-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-brown-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brown-900 text-center mb-12">
            Our Core Values
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-brown-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scale className="w-8 h-8 text-brown-700" />
              </div>
              <h3 className="text-xl font-bold text-brown-900 mb-3">Integrity</h3>
              <p className="text-brown-700">
                We uphold the highest ethical standards in all our legal services and client interactions
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-brown-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-brown-700" />
              </div>
              <h3 className="text-xl font-bold text-brown-900 mb-3">Client-Focused</h3>
              <p className="text-brown-700">
                Your needs come first. We tailor our services to meet your specific legal requirements
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-brown-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-brown-700" />
              </div>
              <h3 className="text-xl font-bold text-brown-900 mb-3">Excellence</h3>
              <p className="text-brown-700">
                We maintain exceptional standards of legal expertise and professional service delivery
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="bg-brown-200 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-brown-700" />
              </div>
              <h3 className="text-xl font-bold text-brown-900 mb-3">Trust</h3>
              <p className="text-brown-700">
                Building lasting relationships based on transparency, reliability, and proven results
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c"
                alt="Lady Justice"
                className="rounded-xl shadow-xl w-full h-[500px] object-cover ring-2 ring-brown-300"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-brown-900 mb-6">Why Choose Us</h2>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Experienced Network</h3>
                    <p className="text-brown-700">
                      Access to licensed attorneys across all 50 states with decades of combined experience
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Transparent Pricing</h3>
                    <p className="text-brown-700">
                      Clear, upfront pricing with no hidden fees or surprise charges
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Personalized Service</h3>
                    <p className="text-brown-700">
                      Tailored legal solutions that address your unique needs and circumstances
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Fast Response</h3>
                    <p className="text-brown-700">
                      Quick consultations and timely responses to keep your case moving forward
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-brown-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Proven Results</h3>
                    <p className="text-brown-700">
                      Track record of successful outcomes and satisfied clients nationwide
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-20 bg-brown-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-brown-900 text-center mb-12">
            Areas of Expertise
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Briefcase className="w-12 h-12 text-brown-700 mb-4" />
              <h3 className="text-xl font-bold text-brown-900 mb-3">Corporate Law</h3>
              <p className="text-brown-700">
                Business formation, contracts, mergers & acquisitions, and corporate compliance
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Target className="w-12 h-12 text-brown-700 mb-4" />
              <h3 className="text-xl font-bold text-brown-900 mb-3">Compliance</h3>
              <p className="text-brown-700">
                Anti-corruption programs, government contracting, and regulatory compliance
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <BookOpen className="w-12 h-12 text-brown-700 mb-4" />
              <h3 className="text-xl font-bold text-brown-900 mb-3">Intellectual Property</h3>
              <p className="text-brown-700">
                Trademark and copyright registration, brand protection, and IP litigation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-brown-900 mb-6">Our Commitment to Excellence</h2>
              <p className="text-lg text-brown-700 leading-relaxed mb-6">
                At IP Partners Inc, excellence isn't just a goal—it's our standard. We are committed to 
                delivering superior legal services that exceed client expectations. Our dedication to continuous 
                improvement and professional development ensures we stay at the forefront of legal innovation.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-brown-200 p-2 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-brown-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Continuous Learning</h3>
                    <p className="text-brown-700">Our attorneys regularly attend seminars and training to stay current with evolving laws</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brown-200 p-2 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-brown-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Client-Centered Approach</h3>
                    <p className="text-brown-700">Every strategy is tailored to meet your specific needs and business objectives</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brown-200 p-2 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-brown-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brown-900 mb-1">Ethical Practice</h3>
                    <p className="text-brown-700">We maintain the highest standards of professional ethics and integrity</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/4427430/pexels-photo-4427430.jpeg"
                alt="Professional legal team"
                className="rounded-xl shadow-xl w-full h-[500px] object-cover ring-2 ring-brown-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industry Recognition Section */}
      <section className="py-20 bg-brown-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-brown-900 mb-4">
              Industry Recognition & Achievements
            </h2>
            <p className="text-lg text-brown-700 max-w-3xl mx-auto">
              Our dedication to legal excellence has earned recognition from industry leaders and satisfied clients nationwide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Award className="w-16 h-16 text-brown-700 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown-900 mb-2">Top Rated</h3>
              <p className="text-brown-700">Legal Services Provider 2024</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Users className="w-16 h-16 text-brown-700 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown-900 mb-2">Best Client</h3>
              <p className="text-brown-700">Service Award Winner</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Scale className="w-16 h-16 text-brown-700 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown-900 mb-2">ABA</h3>
              <p className="text-brown-700">Accredited Member</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <Target className="w-16 h-16 text-brown-700 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-brown-900 mb-2">Excellence</h3>
              <p className="text-brown-700">in Legal Innovation</p>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-xl p-8 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center border-r border-brown-200 last:border-0">
                <div className="text-4xl font-bold text-brown-700 mb-2">25+</div>
                <p className="text-brown-600">Years Combined Experience</p>
              </div>
              <div className="text-center border-r border-brown-200 last:border-0">
                <div className="text-4xl font-bold text-brown-700 mb-2">1000+</div>
                <p className="text-brown-600">Cases Successfully Resolved</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-brown-700 mb-2">$50M+</div>
                <p className="text-brown-600">Client Value Protected</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-brown-900 mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-brown-700 mb-8">
            Let's discuss how we can help you achieve your legal goals
          </p>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-brown-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-brown-800 transition-colors duration-200 shadow-lg"
          >
            Get Started Today
          </button>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default AboutPage;
